import { api } from '../console/api.ts';
import { type CarrierView, carrierCell, carrierName } from '../console/carriers.ts';
import { field, fileField, pageHead, pill } from '../console/components.ts';
import { ApiError, InputError } from '../console/errors.ts';
import { date, esc, fmt, icon, iso, type Money, period, person, when } from '../console/format.ts';
import { type Actions, type ClickAction, current, render } from '../console/router.ts';
import { has, me, supervises } from '../console/session.ts';
import { $, act, centsOf, textInput } from '../console/ui.ts';
import { dateControl, monthControl, selectControl, textControl } from '../controls.ts';
import { type IsrCurrency, isrCurrencies, withholdingOf } from '../manual-isr.ts';

/**
 * «Remesas y conciliación» (Aseguradoras): the remittance of the premium the agency collected for
 * a carrier, the reconciliation of the carrier's statement with the commissions Nubi expects, the
 * statement entered by hand (carriers with no integration) and the disputed commissions.
 */

type RemittanceView = {
  id: string;
  carrier: string;
  basis: string;
  state: string;
  depositReference: string | null;
  depositDate: string | null;
  premium: Money;
  retained: Money;
  net: Money;
  /** Money the carrier already held for the agency (rejected receipts) used by this remittance. */
  balanceApplied: Money;
  /** What the bank deposits: the net less that balance. */
  deposit: Money;
  items: {
    policyNumber: string;
    installment: number;
    premium: Money;
    commissionRetained: Money;
    status: string;
    reasons: string[];
  }[];
};
type ReconciliationView = {
  carrier: string;
  period: string;
  items: {
    status: string;
    accrualId: string | null;
    policyNumber: string;
    installment: number | null;
    expected: Money | null;
    stated: Money | null;
    difference: Money;
  }[];
  claim: Money[];
};
let draft: RemittanceView | null = null;
/** A carrier with premium collected in quetzales and dollars: one remittance per currency. */
let remitCurrencyChoice: string | null = null;
const BASIS: Record<string, string> = {
  GROSS: 'Prima completa',
  NET_OF_COMMISSION: 'Neta de comisión',
};

/** A commission the carrier's statement disputed, and how the agency is resolving it. */
type DisputeView = {
  id: string;
  carrier: string;
  policyNumber: string;
  installment: number | null;
  kind: string;
  agentId: string;
  period: string;
  commission: Money;
  status: string;
  dispute: {
    period: string;
    status: string;
    expected: Money;
    stated: Money;
    difference: Money;
    resolution: {
      decision: 'ACCEPT_CARRIER_AMOUNT' | 'KEEP_CLAIM';
      proposedBy: string;
      proposedOn: string;
      approvedBy: string | null;
      approvedOn: string | null;
    } | null;
  } | null;
};
const RESOLUTION: Record<string, string> = {
  ACCEPT_CARRIER_AMOUNT: 'Aceptar el monto de la aseguradora',
  KEEP_CLAIM: 'Mantener el reclamo',
};

/** The two ways to end a dispute; proposing one leaves it for the direction to approve. */
const proposeButtons = (id: string) =>
  `<button type="button" class="btn sm" data-dispute-accept="${esc(id)}">Aceptar monto de la aseguradora</button>
   <button type="button" class="btn sm" data-dispute-keep="${esc(id)}">Mantener reclamo</button>`;

function disputeActions(a: DisputeView) {
  const r = a.dispute?.resolution;
  if (!r) return supervises() ? proposeButtons(a.id) : '';
  if (r.approvedBy)
    // The claim stands; the agency can still accept what the carrier states later.
    return `${pill('warn', 'Reclamo mantenido')} ${supervises() ? `<button type="button" class="btn sm" data-dispute-accept="${esc(a.id)}">Aceptar monto de la aseguradora</button>` : ''}`;
  const mine = me && r.proposedBy === `human:${me.memberId}`;
  return `<span class="small">Propuesta: ${esc(RESOLUTION[r.decision] ?? r.decision)}</span>
    ${
      has('agency_director') && !mine
        ? `<button type="button" class="btn sm primary" data-dispute-approve="${esc(a.id)}">Aprobar</button>`
        : '<span class="muted small">Espera la aprobación de otra persona de la dirección.</span>'
    }`;
}

function disputesView(list: DisputeView[]) {
  if (!list.length) return '';
  return `<section class="card"><div class="card-head"><h2>Comisiones en disputa</h2><span class="spacer"></span>${pill('warn', `${list.length} por resolver`)}</div>
    <p class="muted small" style="padding:0 20px">La aseguradora no las declaró o las declaró con otro monto. Si las declara en un estado de cuenta posterior se confirman solas; si no, decide aquí: quien propone no aprueba.</p>
    <div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Aseguradora</th><th>Póliza</th><th>Recibo</th><th>Mes</th><th class="num">Esperado</th><th class="num">Declarado</th><th class="num">Diferencia</th><th>Resolución</th></tr></thead><tbody>
    ${list
      .map(
        (
          a,
        ) => `<tr><td>${carrierCell(a.carrier)}</td><td class="mono">${esc(a.policyNumber)}</td><td>${a.installment ?? '—'}</td><td class="nowrap">${esc(period(a.period))}</td>
        <td class="num">${fmt(a.dispute?.expected ?? a.commission)}</td><td class="num">${fmt(a.dispute?.stated ?? null)}</td><td class="num">${a.dispute ? fmt(a.dispute.difference) : '—'}</td>
        <td class="actions">${disputeActions(a)}</td></tr>`,
      )
      .join('')}</tbody></table></div></section>`;
}

/** Reloads the disputes card in place (after reconciling or resolving). */
async function refreshDisputes() {
  const box = document.querySelector('#disputes');
  if (!box || !supervises()) return;
  box.innerHTML = disputesView(await api<DisputeView[]>('GET', '/v1/commission-disputes'));
}

function reconciliationView(r: ReconciliationView) {
  const claim = r.claim.filter((m) => m.amountMinor > 0);
  return `<section class="card"><div class="card-head"><h3>${esc(carrierName(r.carrier))} · ${esc(period(r.period))}</h3><span class="spacer"></span>
    ${
      !r.items.length
        ? pill('info', 'Sin movimientos')
        : r.items.every((i) => i.status === 'MATCHED')
          ? pill('ok', 'Todo coincide')
          : pill('warn', 'Con diferencias')
    }</div>
    ${
      r.items.length
        ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Póliza</th><th>Recibo</th><th class="num">Esperado</th><th class="num">Declarado</th><th class="num">Diferencia</th><th>Estado</th><th class="actions"><span class="sr-only">Resolución</span></th></tr></thead><tbody>
    ${r.items
      .map(
        (
          i,
        ) => `<tr><td class="mono">${esc(i.policyNumber)}</td><td>${i.installment ?? '—'}</td><td class="num">${fmt(i.expected)}</td><td class="num">${fmt(i.stated)}</td>
        <td class="num">${i.difference.amountMinor ? fmt(i.difference) : '—'}</td><td>${pill(i.status)}</td>
        <td class="actions">${i.accrualId && supervises() && (i.status === 'DIFFERENT_AMOUNT' || i.status === 'MISSING_IN_STATEMENT') ? proposeButtons(i.accrualId) : ''}</td></tr>`,
      )
      .join('')}</tbody></table></div>`
        : '<p class="muted" style="padding:20px">El estado de cuenta no trae líneas para ese período.</p>'
    }
    ${claim.length ? `<p class="note">${icon('alert')}<span>Reclamo a la aseguradora: ${claim.map(fmt).join(' + ')} de comisiones omitidas o menores a lo esperado.</span></p>` : ''}</section>`;
}

// ------------------------------------------------------------------ estado de cuenta a mano

/** One version of a statement the agency entered by hand (carriers with no integration). */
type ManualStatementView = {
  carrier: string;
  period: string;
  version: number;
  state: 'PROPOSED' | 'APPROVED' | 'SUPERSEDED' | 'REPLACED';
  lines: {
    policyNumber: string;
    periodStart: string;
    installment: number | null;
    kind: 'COMMISSION' | 'CLAWBACK';
    amount: Money;
  }[];
  withholding: Money[];
  source: { kind: 'TYPED' } | { kind: 'CSV'; fileName: string; sha256: string };
  proposedBy: string;
  proposedOn: string;
  approvedBy: string | null;
  approvedOn: string | null;
};
type ManualDraftLine = {
  policyNumber: string;
  periodStart: string;
  installment: string;
  kind: 'COMMISSION' | 'CLAWBACK';
  amount: string;
  currency: 'GTQ' | 'USD';
};
const LINE_KIND: Record<string, string> = { COMMISSION: 'Comisión', CLAWBACK: 'Reversión' };
/** The carrier whose statement is being entered by hand, its month and what is typed so far. */
let manual: {
  carrier: string;
  period: string;
  versions: ManualStatementView[];
  lines: ManualDraftLine[];
  /** The ISR typed for each currency of the lines. */
  isr: Partial<Record<IsrCurrency, string>>;
} | null = null;
const ISR_LABEL: Record<IsrCurrency, string> = { GTQ: 'Q', USD: 'US$' };
const blankLine = (): ManualDraftLine => ({
  policyNumber: '',
  periodStart: '',
  installment: '',
  kind: 'COMMISSION',
  amount: '',
  currency: 'GTQ',
});
/** The previous calendar month: carriers state a month once it closes. */
const lastMonth = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 15)).toISOString().slice(0, 7);
};

/** Who entered or approved a version, where it came from, and when. */
function provenance(v: ManualStatementView) {
  const from =
    v.source.kind === 'CSV'
      ? `del archivo <span class="mono">${esc(v.source.fileName)}</span> <span class="muted small" title="SHA-256 ${esc(v.source.sha256)}">(huella ${esc(v.source.sha256.slice(0, 12))}…)</span>`
      : 'tecleado en Nubi';
  return `Registrado por <b>${esc(person(v.proposedBy))}</b> el ${esc(date(v.proposedOn))}, ${from}${
    v.approvedBy
      ? `; aprobado por <b>${esc(person(v.approvedBy))}</b> el ${esc(date(v.approvedOn))}`
      : ''
  }.`;
}

function manualLinesTable(v: ManualStatementView) {
  return v.lines.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Póliza</th><th>Vigencia desde</th><th>Recibo</th><th>Tipo</th><th class="num">Monto</th></tr></thead><tbody>
      ${v.lines.map((l) => `<tr><td class="mono">${esc(l.policyNumber)}</td><td class="nowrap">${esc(date(l.periodStart))}</td><td>${l.installment ?? '—'}</td><td>${esc(LINE_KIND[l.kind] ?? l.kind)}</td><td class="num">${fmt(l.amount)}</td></tr>`).join('')}
      </tbody></table></div>${withheld(v)}`
    : `<p class="muted card-pad">Sin movimientos en el mes.</p>${withheld(v)}`;
}

/** The ISR the carrier withheld on a version, in each currency it stated. */
const withheld = (v: ManualStatementView) =>
  v.withholding.length
    ? `<p class="small card-pad">ISR retenido: ${v.withholding.map((w) => `<b class="num">${fmt(w)}</b>`).join(' · ')}</p>`
    : '';

/** «Registrar estado de cuenta»: what is in force, what waits for approval, and the editor. */
function manualSection() {
  if (!manual) return '';
  const m = manual;
  const current = m.versions.find((v) => v.state === 'APPROVED');
  const pending = m.versions.find((v) => v.state === 'PROPOSED');
  const mine = pending && me && pending.proposedBy === `human:${me.memberId}`;
  const input = (i: number, k: keyof ManualDraftLine, attrs: Parameters<typeof textControl>[0]) =>
    textControl({
      'data-manual-k': k,
      label: `${k} de la línea ${i + 1}`,
      hideLabel: true,
      value: String(m.lines[i]?.[k] ?? ''),
      ...attrs,
    });
  const editor = m.lines
    .map(
      (l, i) => `<tr data-manual-row="${i}">
      <td>${input(i, 'policyNumber', { placeholder: 'IZ-GM-000123', autocomplete: 'off' })}</td>
      <td>${input(i, 'periodStart', { type: 'date' })}</td>
      <td>${input(i, 'installment', { inputmode: 'numeric', placeholder: '1', disabled: l.kind === 'CLAWBACK' })}</td>
      <td>${selectControl(
        { 'data-manual-k': 'kind', label: `Tipo de la línea ${i + 1}`, hideLabel: true },
        Object.entries(LINE_KIND).map(([k, name]) => ({
          value: k,
          label: name,
          selected: l.kind === k,
        })),
      )}</td>
      <td>${input(i, 'amount', { inputmode: 'decimal', placeholder: '36.00' })}</td>
      <td>${selectControl(
        { 'data-manual-k': 'currency', label: `Moneda de la línea ${i + 1}`, hideLabel: true },
        [
          { value: 'GTQ', label: 'Q', selected: l.currency === 'GTQ' },
          { value: 'USD', label: 'US$', selected: l.currency === 'USD' },
        ],
      )}</td>
      <td class="actions"><button type="button" class="btn sm" data-manual-remove="${i}" aria-label="Quitar la línea ${i + 1}">Quitar</button></td></tr>`,
    )
    .join('');
  return `<section class="card" aria-label="Estado de cuenta registrado a mano"><div class="card-head"><h2>Estado de cuenta de ${esc(carrierName(m.carrier))} · ${esc(period(m.period))}</h2><span class="spacer"></span>
      ${current ? pill('ok', `Vigente: versión ${current.version}`) : pill('info', 'Sin estado de cuenta aprobado')}
      <button type="button" class="btn sm" data-manual-close="1">Cerrar</button></div>
    <p class="muted small card-pad">${esc(carrierName(m.carrier))} no envía su estado de cuenta por sistema. Regístralo como viene en el papel o la hoja de la aseguradora; la dirección lo aprueba (quien lo registra no lo aprueba) y después se concilia como cualquier otro.</p>
    <div class="toolbar">${field('Período', monthControl({ id: 'manual-period', value: m.period }))}
      <button type="button" class="btn" data-manual-load="1">Ver ese mes</button></div>
    ${current ? `<h3 class="split-title card-pad">Versión ${current.version} aprobada</h3><p class="small card-pad">${provenance(current)}</p>${manualLinesTable(current)}` : ''}
    ${
      pending
        ? `<div class="toolbar"><div class="grow"><strong>Versión ${pending.version} espera aprobación</strong><span class="small">${provenance(pending)}</span></div>
          ${has('agency_director') && !mine ? `<button type="button" class="btn primary" data-manual-approve="1">Aprobar versión ${pending.version}</button>` : '<span class="muted small">Espera la aprobación de otra persona de la dirección.</span>'}</div>${manualLinesTable(pending)}`
        : ''
    }
    <h3 class="split-title card-pad">${current || pending ? 'Registrar una nueva versión' : 'Registrar las líneas del mes'}</h3>
    <div class="table-wrap" data-component="table"><table class="table line-editor"><thead><tr><th>Póliza</th><th>Vigencia desde</th><th>Recibo</th><th>Tipo</th><th>Monto</th><th>Moneda</th><th class="actions"><span class="sr-only">Quitar</span></th></tr></thead>
      <tbody>${editor}</tbody></table></div>
    <div class="toolbar"><button type="button" class="btn" data-manual-add="1">Agregar línea</button>
      ${isrCurrencies(m.lines)
        .map((c) =>
          field(
            `ISR retenido (${ISR_LABEL[c]})`,
            textControl({
              id: `manual-isr-${c}`,
              'data-manual-isr': c,
              inputmode: 'decimal',
              placeholder: '0.00',
              value: m.isr[c] ?? '',
            }),
          ),
        )
        .join('')}
      <span class="spacer"></span>
      <button type="button" class="btn primary" data-manual-submit="1">Registrar para aprobación</button></div>
    <div class="toolbar"><div class="grow"><strong>O sube la hoja en CSV</strong><span class="muted small">Columnas: poliza, vigencia (AAAA-MM-DD), recibo (vacío en una reversión), tipo (COMISION o REVERSION), monto (1234.56) y moneda (GTQ o USD). Nubi guarda la huella del archivo.</span></div>
      ${fileField('Archivo CSV', { id: 'manual-file', accept: '.csv,text/csv', hint: 'CSV · hasta 400 KB' })}
      <button type="button" class="btn" data-manual-csv="1">${icon('upload')}Subir CSV</button></div></section>`;
}

/** Keeps what the person typed in the editor before the section is painted again. */
function readManualLines() {
  if (!manual) return;
  manual.lines = [...document.querySelectorAll<HTMLElement>('[data-manual-row]')].map((row) => {
    const v = (k: string) =>
      (row.querySelector(`[data-manual-k="${k}"]`) as HTMLInputElement | null)?.value.trim() ?? '';
    return {
      policyNumber: v('policyNumber'),
      periodStart: v('periodStart'),
      installment: v('installment'),
      kind: v('kind') === 'CLAWBACK' ? 'CLAWBACK' : 'COMMISSION',
      amount: v('amount'),
      currency: v('currency') === 'USD' ? 'USD' : 'GTQ',
    };
  });
  for (const el of document.querySelectorAll<HTMLInputElement>('[data-manual-isr]'))
    manual.isr[el.dataset.manualIsr as IsrCurrency] = el.value.trim();
}

async function loadManual(carrier: string, p: string) {
  const r = await api<{ versions: ManualStatementView[] }>(
    'GET',
    `/v1/statements/${encodeURIComponent(carrier)}/${encodeURIComponent(p)}/manual`,
  );
  manual = {
    carrier,
    period: p,
    versions: r.versions,
    lines: manual?.carrier === carrier && manual.lines.length ? manual.lines : [blankLine()],
    isr: manual?.carrier === carrier ? manual.isr : {},
  };
}
const paintManual = () => {
  const box = document.querySelector('#manual-box');
  if (box) box.innerHTML = manualSection();
};

export async function aseguradoras() {
  const [carriers, remittances, disputes, notices] = await Promise.all([
    api<CarrierView[]>('GET', '/v1/carriers'),
    api<RemittanceView[]>('GET', '/v1/remittances'),
    supervises() ? api<DisputeView[]>('GET', '/v1/commission-disputes') : Promise.resolve([]),
    supervises() ? api<NoticeView[]>('GET', '/v1/carrier-notices') : Promise.resolve(null),
  ]);
  let html = pageHead(
    'Remesas y conciliación',
    'Entrega a cada aseguradora la prima que cobró la agencia y compara su estado de cuenta con las comisiones que Nubi espera.',
  );
  html += `<section class="card"><div class="card-head"><h2>Aseguradoras</h2></div><div class="table-wrap" data-component="table"><table class="table">
    <thead><tr><th>Aseguradora</th><th>Quién cobra</th><th>Remesa</th><th>Estado de cuenta</th><th class="actions"><span class="sr-only">Acción</span></th></tr></thead><tbody>
    ${carriers
      .map(
        (
          c,
        ) => `<tr><td>${carrierCell(c.id, c.name)}</td><td>${c.collectedBy === 'AGENCY' ? 'La agencia' : c.collectedBy === 'CARRIER' ? 'La aseguradora' : '—'}</td>
        <td>${c.remittance ? `${esc(BASIS[c.remittance.basis] ?? c.remittance.basis)}${c.remittance.byDeposit ? ' <span class="muted small">(por depósito)</span>' : ''}` : '<span class="muted">No aplica</span>'}</td>
        <td>${c.statement ? 'Por sistema, cada mes <div class="muted small">Se lee al conciliar; nadie lo captura</div>' : '<span class="muted">Sin integración: se registra a mano</span>'}</td>
        <td class="actions">${c.remittance && c.collectedBy === 'AGENCY' ? `<button type="button" class="btn sm" data-remit-prepare="${esc(c.id)}">Preparar remesa</button>` : ''}${!c.statement && supervises() ? ` <button type="button" class="btn sm" data-manual-open="${esc(c.id)}">Registrar estado de cuenta</button>` : ''}</td></tr>`,
      )
      .join('')}</tbody></table></div></section>`;
  if (remitCurrencyChoice && !draft)
    html += `<section class="card toolbar" aria-label="Moneda de la remesa"><div class="grow"><strong>Remesa para ${esc(carrierName(remitCurrencyChoice))}</strong>
      <span class="muted small">Hay primas cobradas en quetzales y en dólares. Cada moneda se deposita por separado: prepara una remesa por moneda.</span></div>
      <button type="button" class="btn" data-remit-prepare="${esc(remitCurrencyChoice)}" data-remit-currency="GTQ">Remesa en quetzales</button>
      <button type="button" class="btn" data-remit-prepare="${esc(remitCurrencyChoice)}" data-remit-currency="USD">Remesa en dólares</button></section>`;
  if (draft)
    html += `<section class="card"><div class="card-head"><h2>Remesa para ${esc(carrierName(draft.carrier))}</h2><span class="spacer"></span>${pill('DRAFT')}</div>
      <div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Póliza</th><th>Recibo</th><th class="num">Prima</th><th class="num">Comisión retenida</th></tr></thead><tbody>
      ${draft.items.map((i) => `<tr><td class="mono">${esc(i.policyNumber)}</td><td>${i.installment}</td><td class="num">${fmt(i.premium)}</td><td class="num">${fmt(i.commissionRetained)}</td></tr>`).join('')}
      <tr><td colspan="2"><strong>Neto (${esc(BASIS[draft.basis] ?? draft.basis)})</strong></td><td class="num"><strong>${fmt(draft.premium)}</strong></td><td class="num"><strong>${fmt(draft.net)}</strong> neto</td></tr>
      ${draft.balanceApplied.amountMinor ? `<tr><td colspan="3">Saldo a favor que ya tiene la aseguradora (recibos rechazados)</td><td class="num">− ${fmt(draft.balanceApplied)}</td></tr>` : ''}
      <tr><td colspan="3"><strong>A depositar</strong></td><td class="num"><strong>${fmt(draft.deposit)}</strong></td></tr>
      </tbody></table></div>
      <div class="toolbar"><div class="grow"><strong>Depósito</strong><span class="muted small">${draft.deposit.amountMinor ? `Deposita ${fmt(draft.deposit)} en la cuenta de la aseguradora y registra la boleta.` : 'La aseguradora ya tiene el dinero: no hay que depositar. Registra una referencia de la remesa.'}${carriers.find((c) => c.id === draft?.carrier)?.remittance?.byDeposit ? ' Esta aseguradora no recibe remesas por sistema: al registrar la boleta, la remesa queda aplicada.' : ''}</span></div>
        ${field('Boleta', textControl({ id: 'remit-ref', placeholder: 'BI-0000000' }))}
        ${field('Fecha', dateControl({ id: 'remit-date', value: iso(new Date()) }))}
        <button type="button" class="btn danger" data-remit-annul="${esc(draft.id)}">Anular</button>
        <button type="button" class="btn primary" data-remit-submit="${esc(draft.id)}">Enviar remesa</button></div></section>`;
  html += remittances.length
    ? `<section class="card"><div class="card-head"><h2>Remesas</h2></div><div class="table-wrap" data-component="table"><table class="table">
      <thead><tr><th>Aseguradora</th><th>Boleta</th><th>Fecha</th><th class="num">Recibos</th><th class="num">Prima</th><th class="num">Neto depositado</th><th>Estado</th></tr></thead><tbody>
      ${remittances
        // A draft left unsent (not the one being prepared above) holds its receipts: it can
        // be annulled here.
        .filter((r) => r.id !== draft?.id)
        .map(
          (
            r,
          ) => `<tr><td>${carrierCell(r.carrier)}</td><td class="mono">${esc(r.depositReference ?? '—')}</td><td class="nowrap">${esc(date(r.depositDate))}</td>
          <td class="num">${r.items.length}</td><td class="num">${fmt(r.premium)}</td><td class="num">${fmt(r.deposit ?? r.net)}</td><td>${pill(r.state)}${r.state === 'DRAFT' || (r.state === 'SUBMITTED' && carriers.find((c) => c.id === r.carrier)?.remittance?.byDeposit) ? ` <button type="button" class="btn sm danger" data-remit-annul="${esc(r.id)}">Anular</button>` : ''}</td></tr>`,
        )
        .join('')}</tbody></table></div></section>`
    : '';
  html += `<div id="manual-box">${manualSection()}</div>`;
  // Carriers without an integration are reconciled against the statement entered by hand.
  html += `<section class="card toolbar" aria-label="Conciliar estado de cuenta">
    <div class="grow"><strong>Conciliar estado de cuenta</strong><span class="muted small">Compara las comisiones que declara la aseguradora con las que Nubi calculó.</span></div>
    ${field(
      'Aseguradora',
      selectControl(
        { id: 'rec-carrier' },
        carriers.map((c) => ({
          value: c.id,
          label: `${c.name}${c.statement ? '' : ' (registrado a mano)'}`,
        })),
      ),
    )}
    ${field('Período', monthControl({ id: 'rec-period', value: lastMonth() }))}
    <button type="button" class="btn primary" id="reconcile" data-reconcile>Conciliar</button></section>
    <div id="rec-result"></div><div id="disputes">${disputesView(disputes)}</div>`;
  if (notices) html += noticesView(notices);
  return html;
}

// ------------------------------------------------------------------ avisos de las aseguradoras

/** A notice a carrier sent the agency (GET /v1/carrier-notices, ADR-0022). */
type NoticeView = {
  carrier: string;
  eventId: string;
  type: string;
  reference: string | null;
  receivedAt: string;
  status: 'RECEIVED' | 'APPLIED';
  outcome: string | null;
  duplicates: number;
};

/** The notices in the agency's words (never the carrier's codes on screen). */
const NOTICE: Record<string, string> = {
  ISSUANCE_ACCEPTED: 'Solicitud aceptada',
  ISSUANCE_REJECTED: 'Solicitud rechazada',
  INSTALLMENT_PAID: 'Requerimiento pagado',
  POLICY_EXPIRING: 'Póliza por vencer',
  POLICY_RENEWED: 'Póliza renovada',
  ENDORSEMENT_APPLIED: 'Endoso aplicado',
  ENDORSEMENT_DECLINED: 'Endoso rechazado',
  CLAIM_STATUS_CHANGED: 'Siniestro actualizado',
  CLAIM_PAYMENT: 'Pago de siniestro',
};

/**
 * «Avisos de las aseguradoras»: what each carrier told the agency on its own (signed notices) and
 * what Nubi did with it. Direction and administration only.
 */
function noticesView(notices: NoticeView[]) {
  const body = notices.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Recibido</th><th>Aseguradora</th><th>Aviso</th><th>Referencia</th><th>Qué hizo Nubi</th><th>Estado</th></tr></thead><tbody>
      ${notices
        .slice(0, 30)
        .map(
          (
            n,
          ) => `<tr data-notice="${esc(n.eventId)}"><td class="nowrap">${esc(when(n.receivedAt))}${n.duplicates ? `<div class="muted small">llegó ${n.duplicates + 1} veces</div>` : ''}</td>
          <td>${carrierCell(n.carrier)}</td><td>${esc(NOTICE[n.type] ?? 'Aviso')}</td>
          <td class="mono">${esc(n.reference ?? '—')}</td><td>${esc(n.outcome ?? 'Por aplicar')}</td>
          <td>${n.status === 'APPLIED' ? pill('ok', 'Aplicado') : pill('warn', 'Por aplicar')}</td></tr>`,
        )
        .join('')}</tbody></table></div>`
    : `<p class="note muted" data-component="notices-empty">${icon('bell')}Todavía no llegan avisos. Cuando una aseguradora acepte una solicitud, reciba un pago o aplique un endoso, aparecerá aquí.</p>`;
  return `<section class="card" id="carrier-notices" aria-label="Avisos de las aseguradoras" data-component="carrier-notices">
    <div class="card-head"><h2>Avisos de las aseguradoras</h2><span class="spacer"></span><span class="muted small">Firmados por la aseguradora; Nubi los aplica al llegar</span></div>${body}</section>`;
}

/** Forgets the remittance being prepared and the statement being entered (a new session). */
export function resetRemittances() {
  draft = null;
  remitCurrencyChoice = null;
  manual = null;
}

/** Proposes how to end a dispute (accept the carrier's amount or keep the claim). */
const proposeResolution: ClickAction = (t, d) => {
  const accept = Boolean(d.disputeAccept);
  const id = d.disputeAccept ?? d.disputeKeep ?? '';
  void act(t, 'Proponiendo la resolución…', async () => {
    await api('POST', `/v1/commission-accruals/${encodeURIComponent(id)}/resolution`, {
      decision: accept ? 'ACCEPT_CARRIER_AMOUNT' : 'KEEP_CLAIM',
    });
    await refreshDisputes();
    return accept
      ? 'Propuesta registrada: aceptar el monto de la aseguradora. Falta que la dirección la apruebe.'
      : 'Propuesta registrada: mantener el reclamo. Falta que la dirección la apruebe.';
  });
};

export const actions: Actions = {
  click: {
    remitPrepare: (t, d) => {
      void act(t, 'Preparando la remesa…', async () => {
        const carrier = d.remitPrepare ?? '';
        let r: RemittanceView;
        try {
          r = await api<RemittanceView>('POST', '/v1/remittances', {
            carrier,
            ...(d.remitCurrency ? { currency: d.remitCurrency } : {}),
          });
        } catch (e) {
          if (!(e instanceof ApiError) || e.code !== 'CURRENCY_REQUIRED') throw e;
          remitCurrencyChoice = carrier;
          await render(current, { quiet: true });
          return 'Hay primas en quetzales y en dólares: elige la moneda de la remesa.';
        }
        draft = r;
        remitCurrencyChoice = null;
        await render(current, { quiet: true });
        return `Remesa preparada: ${r.items.length} recibos, ${fmt(r.deposit)} a depositar.`;
      });
    },
    remitAnnul: (t, d) => {
      void act(t, 'Anulando la remesa…', async () => {
        await api('POST', `/v1/remittances/${encodeURIComponent(d.remitAnnul ?? '')}/annul`);
        if (draft?.id === d.remitAnnul) draft = null;
        await render(current, { quiet: true });
        return 'Remesa anulada: sus recibos quedan para la siguiente remesa.';
      });
    },
    remitSubmit: (t, d) => {
      void act(t, 'Enviando la remesa a la aseguradora…', async () => {
        const depositReference = textInput('#remit-ref', 'la boleta de depósito');
        const depositDate = textInput('#remit-date', 'la fecha del depósito');
        const r = await api<RemittanceView>(
          'POST',
          `/v1/remittances/${encodeURIComponent(d.remitSubmit ?? '')}/submit`,
          { depositReference, depositDate },
        );
        draft = null;
        await render(current, { quiet: true });
        const rejected = r.items.filter((i) => i.status === 'REJECTED').length;
        return rejected
          ? `Remesa enviada; la aseguradora rechazó ${rejected} recibos.`
          : 'Remesa enviada y aplicada por la aseguradora.';
      });
    },
    manualOpen: (t, d) => {
      void act(t, 'Abriendo el estado de cuenta…', async () => {
        await loadManual(d.manualOpen ?? '', manual?.period ?? lastMonth());
        paintManual();
        $('#manual-box')?.scrollIntoView({ block: 'start' });
        return `Estado de cuenta de ${carrierName(d.manualOpen ?? '')} abierto.`;
      });
    },
    manualClose: () => {
      manual = null;
      paintManual();
    },
    manualLoad: (t) => {
      void act(t, 'Leyendo el estado de cuenta…', async () => {
        if (!manual) return '';
        readManualLines();
        await loadManual(manual.carrier, textInput('#manual-period', 'el período'));
        paintManual();
        return `Estado de cuenta de ${period(manual.period)}.`;
      });
    },
    manualAdd: () => {
      readManualLines();
      manual?.lines.push(blankLine());
      paintManual();
      const rows = document.querySelectorAll<HTMLInputElement>('[data-manual-k="policyNumber"]');
      rows[rows.length - 1]?.focus();
    },
    manualRemove: (_, d) => {
      readManualLines();
      manual?.lines.splice(Number(d.manualRemove), 1);
      if (manual && !manual.lines.length) manual.lines.push(blankLine());
      paintManual();
    },
    manualSubmit: (t) => {
      void act(t, 'Registrando el estado de cuenta…', async () => {
        if (!manual) return '';
        readManualLines();
        const m = manual;
        const lines = m.lines
          .filter((l) => l.policyNumber || l.amount)
          .map((l, i) => {
            const n = i + 1;
            if (!l.policyNumber) throw new InputError(`Falta la póliza en la línea ${n}.`);
            if (!l.periodStart) throw new InputError(`Falta la vigencia en la línea ${n}.`);
            const cents = centsOf(l.amount, false);
            if (!cents) throw new InputError(`Falta el monto en la línea ${n}.`);
            if (l.kind === 'COMMISSION' && !/^\d+$/.test(l.installment))
              throw new InputError(`Falta el número de recibo en la línea ${n}.`);
            return {
              policyNumber: l.policyNumber,
              periodStart: l.periodStart,
              installment: l.kind === 'COMMISSION' ? Number(l.installment) : null,
              kind: l.kind,
              amount: { amountMinor: cents, currency: l.currency },
            };
          });
        const withholding = withholdingOf(
          m.isr,
          isrCurrencies(lines.map((l) => l.amount)),
          (text) => centsOf(text, false),
        );
        const v = await api<ManualStatementView>(
          'POST',
          `/v1/statements/${encodeURIComponent(m.carrier)}/${encodeURIComponent(m.period)}/manual`,
          { lines, withholding },
        );
        m.lines = [blankLine()];
        m.isr = {};
        await loadManual(m.carrier, m.period);
        paintManual();
        return `Versión ${v.version} registrada con ${v.lines.length} líneas. Falta que la dirección la apruebe.`;
      });
    },
    manualCsv: (t) => {
      void act(t, 'Leyendo el CSV…', async () => {
        if (!manual) return '';
        readManualLines();
        const m = manual;
        const file = ($('#manual-file') as HTMLInputElement).files?.[0];
        if (!file) throw new InputError('Elige el archivo CSV del estado de cuenta.');
        if (!/\.csv$/i.test(file.name)) throw new InputError('El archivo debe ser .csv.');
        if (file.size > 400 * 1024) throw new InputError('El CSV pesa más de 400 KB.');
        const v = await api<ManualStatementView>(
          'POST',
          `/v1/statements/${encodeURIComponent(m.carrier)}/${encodeURIComponent(m.period)}/manual`,
          { fileName: file.name, csv: await file.text() },
        );
        await loadManual(m.carrier, m.period);
        paintManual();
        return `Versión ${v.version} registrada desde ${file.name} con ${v.lines.length} líneas. Falta que la dirección la apruebe.`;
      });
    },
    manualApprove: (t) => {
      void act(t, 'Aprobando el estado de cuenta…', async () => {
        if (!manual) return '';
        readManualLines();
        const m = manual;
        const v = await api<ManualStatementView>(
          'POST',
          `/v1/statements/${encodeURIComponent(m.carrier)}/${encodeURIComponent(m.period)}/manual/approve`,
        );
        await loadManual(m.carrier, m.period);
        paintManual();
        return `Versión ${v.version} aprobada: ya se puede conciliar ${period(m.period)}.`;
      });
    },
    reconcile: (t) => {
      void act(t, 'Conciliando el estado de cuenta…', async () => {
        const carrier = ($('#rec-carrier') as HTMLSelectElement).value;
        const p = textInput('#rec-period', 'el período');
        const r = await api<ReconciliationView>(
          'POST',
          `/v1/statements/${encodeURIComponent(carrier)}/${encodeURIComponent(p)}/reconcile`,
        );
        $('#rec-result').innerHTML = reconciliationView(r);
        await refreshDisputes();
        const off = r.items.filter((i) => i.status !== 'MATCHED').length;
        return off
          ? `${off} de ${r.items.length} líneas no coinciden. Revisa el detalle.`
          : `Las ${r.items.length} líneas coinciden.`;
      });
    },
    disputeAccept: proposeResolution,
    disputeKeep: proposeResolution,
    disputeApprove: (t, d) => {
      void act(t, 'Aprobando la resolución…', async () => {
        const r = await api<DisputeView & { adjustment: DisputeView | null }>(
          'POST',
          `/v1/commission-accruals/${encodeURIComponent(d.disputeApprove ?? '')}/resolution/approve`,
        );
        await refreshDisputes();
        return r.status === 'CONFIRMED'
          ? `Resolución aprobada: la comisión se liquidará por lo que paga la aseguradora${r.adjustment ? ` (ajuste de ${fmt(r.adjustment.commission)})` : ''}.`
          : 'Resolución aprobada: la agencia mantiene el reclamo a la aseguradora.';
      });
    },
  },
  changeData: {
    manualK: (control) => {
      if (control.dataset.manualK !== 'currency') return;
      // The ISR is asked for each currency of the lines: a line's currency changes the fields.
      const row = control.closest<HTMLElement>('[data-manual-row]')?.dataset.manualRow;
      readManualLines();
      paintManual();
      $(`[data-manual-row="${row}"] [data-manual-k="currency"]`)?.focus();
    },
  },
};
